export type ExtraTracingLevel = {
  id: string;
  title: string;
  hint: string;
  path?: string;
  glyph?: string;
  difficulty: "L2" | "L3" | "L4";
  icon: string;
  group: string;
};

const glyphLesson = (
  id: string,
  glyph: string,
  difficulty: "L2" | "L3" | "L4",
  group: string,
  title: string,
  hint: string,
): ExtraTracingLevel => ({ id, glyph, difficulty, group, title, hint, icon: glyph });

export const numberTracingLessons: ExtraTracingLevel[] = Array.from({ length: 11 }, (_, number) =>
  glyphLesson(
    `number-${number}`,
    String(number),
    "L2",
    "数字0–10",
    `数字 ${number} 的星光笔顺`,
    number === 10 ? "先写1，再写0，分两笔完成" : "沿着数字轮廓，慢慢写得又稳又清楚",
  ),
);

const complexShapes: ExtraTracingLevel[] = [
  { id:"shape-hexagon",title:"六边形蜂巢",hint:"六条边长度尽量一样",path:"M350 70 L535 165 L535 315 L350 410 L165 315 L165 165 Z",difficulty:"L3",icon:"⬡",group:"复杂图形" },
  { id:"shape-cube",title:"立体魔方",hint:"先画外框，再连接三个方向",path:"M350 55 L545 165 L350 275 L155 165 Z M155 165 L155 365 L350 455 L350 275 M545 165 L545 365 L350 455",difficulty:"L3",icon:"▣",group:"复杂图形" },
  { id:"shape-prism",title:"三角水晶",hint:"找到顶点，连接每一条棱",path:"M350 45 L585 380 L115 380 Z M350 45 L350 380 M115 380 L350 245 L585 380",difficulty:"L3",icon:"△",group:"复杂图形" },
  { id:"shape-rosette",title:"八瓣星花",hint:"围绕中心画出对称花瓣",path:"M350 240 C255 135 320 40 350 160 C380 40 445 135 350 240 C455 145 550 210 430 240 C550 270 455 335 350 240 C445 345 380 440 350 320 C320 440 255 345 350 240 C245 335 150 270 270 240 C150 210 245 145 350 240",difficulty:"L3",icon:"✺",group:"复杂图形" },
  { id:"shape-celtic",title:"交叉丝带结",hint:"穿过交叉点时保持方向",path:"M115 155 C210 45 310 115 350 240 C390 365 490 435 585 325 M585 155 C490 45 390 115 350 240 C310 365 210 435 115 325",difficulty:"L3",icon:"∞",group:"复杂图形" },
  { id:"shape-snowflake",title:"六角雪花",hint:"从中心向六个方向伸展",path:"M350 45 V435 M180 145 L520 335 M520 145 L180 335 M350 110 L315 145 M350 110 L385 145 M350 370 L315 335 M350 370 L385 335",difficulty:"L3",icon:"❄",group:"复杂图形" },
  { id:"shape-maze",title:"星际回旋迷宫",hint:"沿着回旋通道走到中心",path:"M95 95 H605 V405 H155 V155 H545 V345 H215 V215 H485 V285 H350",difficulty:"L3",icon:"⌘",group:"复杂图形" },
  { id:"shape-constellation",title:"星座连接图",hint:"按顺序连接每一颗星",path:"M95 350 L175 125 L275 285 L350 75 L430 260 L535 115 L615 350 L475 390 L350 315 L225 395 Z",difficulty:"L3",icon:"✧",group:"复杂图形" },
];

const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) =>
  glyphLesson(`letter-upper-${letter.toLowerCase()}`, letter, "L3", "大写字母A–Z", `大写字母 ${letter}`, "看清起笔位置，按笔顺分段描写"),
);
const lowercase = "abcdefghijklmnopqrstuvwxyz".split("").map((letter) =>
  glyphLesson(`letter-lower-${letter}`, letter, "L3", "小写字母a–z", `小写字母 ${letter}`, "注意高矮和圆弧，抬笔后可以继续下一笔"),
);

const hanzi = [
  ["一","横平星光线"],["二","两层云梯"],["三","三层星光桥"],["十","十字路口"],["人","小人迈开步"],
  ["大","张开大手臂"],["小","小小星芽"],["上","向上看星星"],["下","向下找脚印"],["中","走进正中间"],
  ["口","方方小窗口"],["日","太阳方格"],["月","月亮小门"],["山","三座小山峰"],["水","小河分支流"],
  ["火","火苗跳跳舞"],["木","大树伸枝叶"],["田","四块小田地"],["土","泥土长新芽"],["天","天空张开手"],
  ["云","云朵轻轻飘"],["雨","雨点落下来"],["子","孩子学走路"],["女","女孩转个弯"],["好","友好在一起"],
  ["手","小手会创造"],["目","眼睛看世界"],["耳","耳朵听声音"],["力","用力向前走"],["心","爱心亮起来"],
] as const;

export const hanziTracingLessons: ExtraTracingLevel[] = hanzi.map(([character, title], index) =>
  glyphLesson(`hanzi-${index + 1}`, character, "L4", "幼小衔接基础汉字", title, "听一听笔顺提示，一笔一笔写完整"),
);

export const prewritingTracingLessons: ExtraTracingLevel[] = [
  ...numberTracingLessons,
  ...complexShapes,
  ...uppercase,
  ...lowercase,
  ...hanziTracingLessons,
];
